import {CdkScrollable, ScrollDispatcher} from '@angular/cdk/overlay';
import {AsyncPipe, TitleCasePipe} from '@angular/common';
import {Component, ElementRef, Input, NgZone, OnInit, ViewChild} from '@angular/core';
import {MatIcon} from '@angular/material/icon';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {AssetService} from '@app/asset';
import {FilterService} from '@app/assetfilter/filter.service';
import {BadgeComponent} from '@app/badge/badge/badge.component';

import {InspectorService} from '@app/inspector/inspector.service';
import {PreferencesService} from '@app/preferences/preferences.service';
import {SelectionMode, SelectionService} from '@app/selection/selection.service';
import {StateService} from '@app/state/state.service';
import {DateAgoPipe} from '@shared/date-ago.pipe';
import {Rect} from '@shared/euclid';
import {IdenticonPipe} from '@shared/identicon.pipe';
import {Observable, Subscription} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {Asset, AssetColorMap, AssetType, IconMap} from '../models';

@Component({
    selector: 'frog-asset-thumbnail',
    templateUrl: './asset-thumbnail.component.html',
    styleUrls: ['./asset-thumbnail.component.scss'],
    imports: [
        MatIcon,
        BadgeComponent,
        RouterLink,
        AsyncPipe,
        TitleCasePipe,
        IdenticonPipe,
        DateAgoPipe,
    ],
})
export class AssetThumbnailComponent implements OnInit {
    @ViewChild('#img') img: ElementRef;
    @Input() asset: Asset;

    public thumbnail: string;
    public loaded: boolean;
    public selected$: Observable<boolean>;
    public selectionMode$: Observable<boolean>;
    public streamId$: Observable<string>;
    public inspected$: Observable<boolean>;
    public queryParams$: Observable<any>;
    public search: string;
    public AssetType = AssetType;
    public IconMap = IconMap;
    public AssetColorMap = AssetColorMap;
    private rect: Rect;
    private scrollSub: Subscription;

    constructor(
        private elementRef: ElementRef,
        private inspectorService: InspectorService,
        private assetService: AssetService,
        private scrollService: ScrollDispatcher,
        private selectionService: SelectionService,
        private stateService: StateService,
        private preferencesService: PreferencesService,
        private filterService: FilterService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private zone: NgZone,
    ) {
        this.thumbnail = 'assets/pixel.png';
        this.loaded = false;
    }

    ngOnInit(): void {
        this.scrollSub = this.scrollService.scrolled(500).subscribe((data: CdkScrollable) => {
            if (!data) {
                return;
            }
            this.viewportCheck(data.measureScrollOffset('top'));
        });
        this.assetService.assetUpdated
            .pipe(filter((asset) => asset.id === this.asset.id))
            .subscribe((asset) => {
                this.asset = asset;
                this.thumbnail = this.asset.thumbnail;
            });

        setTimeout(() => this.viewportCheck(), 0);

        this.streamId$ = this.stateService.state.pipe(map((state) => state.streamId));

        this.inspected$ = this.inspectorService.asset.pipe(
            map((asset) => {
                if (asset === null) {
                    return false;
                }

                return asset.id === this.asset.id;
            }),
        );

        this.selected$ = this.selectionService.selection.pipe(
            map((assetIds) => assetIds.includes(this.asset.id)),
        );
        this.selectionMode$ = this.selectionService.selectionMode;
        this.queryParams$ = this.activatedRoute.queryParams.pipe(
            map((p) => {
                return { q: decodeURIComponent(window.location.search.substr(3)) };
            }),
        );
    }

    viewportCheck(y: number = 0) {
        const rect = new Rect(0, y, window.innerWidth, window.innerHeight);
        this.rect = new Rect(
            this.elementRef.nativeElement.offsetLeft,
            this.elementRef.nativeElement.offsetTop,
            this.elementRef.nativeElement.offsetWidth,
            this.elementRef.nativeElement.offsetHeight,
        );
        if (rect.intersects(this.rect)) {
            this.zone.run(() => {
                this.load();
            });
        }
    }

    load() {
        this.loaded = true;
        this.thumbnail = this.asset.thumbnail;
        this.scrollSub.unsubscribe();
    }

    loadError() {
        this.thumbnail = 'assets/missing_image.png';
    }

    inspect() {
        this.inspectorService.inspect(this.asset);
    }

    filterByUser() {
        this.filterService.patchFilterData('users', [this.asset.author.email], true);
    }

    selectAsset(selected: boolean) {
        const mode = selected ? SelectionMode.Remove : SelectionMode.Add;
        this.selectionService.setSelection([this.asset], mode);
    }

    getLink(streamId: string = null): string[] {
        if (this.asset.asset_type === AssetType.Review) {
            return ['/r', this.asset.id];
        }

        if (streamId) {
            return ['/v', streamId, this.asset.id];
        } else {
            return ['/v', this.asset.id];
        }
    }
}
